import { useCallback, useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState, ConfirmDialog, StatusBadge } from "@/components/common";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Check, X, Trash, CircleNotch, Sliders } from "@phosphor-icons/react";

const inputCls = "rounded-sm border-input bg-background text-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0";

const TYPES = [
  { value: "vacaciones", label: "Vacaciones" },
  { value: "permiso_goce", label: "Permiso con goce" },
  { value: "permiso_sin_goce", label: "Permiso sin goce" },
  { value: "incapacidad", label: "Incapacidad" },
];
const typeLabel = (v) => TYPES.find((t) => t.value === v)?.label || v;

export default function Solicitudes() {
  const [requests, setRequests] = useState(null);
  const [balances, setBalances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ employee_id: "", type: "vacaciones", start_date: "", end_date: "", reason: "" });
  const [resolve, setResolve] = useState(null); // {req, status}
  const [comment, setComment] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [adjust, setAdjust] = useState(null); // balance row
  const [adjustForm, setAdjustForm] = useState({ adjustment_days: "", note: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    Promise.all([api.get("/leave-requests"), api.get("/vacations/all"), api.get("/employees")])
      .then(([r, b, e]) => {
        setRequests(Array.isArray(r.data) ? r.data : []);
        setBalances(Array.isArray(b.data) ? b.data : []);
        setEmployees((Array.isArray(e.data) ? e.data : []).filter((x) => x.active));
      })
      .catch((err) => {
        toast.error(apiError(err));
        setRequests([]);
      });
  }, []);

  useEffect(load, [load]);

  const filtered = useMemo(() => {
    if (!requests) return [];
    if (filter === "all") return requests;
    return requests.filter((r) => r.status === filter);
  }, [requests, filter]);

  const doResolve = async () => {
    setSaving(true);
    try {
      await api.post(`/leave-requests/${resolve.req.id}/resolve`, { status: resolve.status, admin_comment: comment });
      toast.success(resolve.status === "approved" ? "Solicitud aprobada" : "Solicitud rechazada");
      setResolve(null);
      setComment("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/leave-requests", { ...form, employee_id: form.employee_id || null });
      toast.success("Solicitud creada");
      setCreateOpen(false);
      setForm({ employee_id: "", type: "vacaciones", start_date: "", end_date: "", reason: "" });
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/leave-requests/${toDelete.id}`);
      toast.success("Solicitud eliminada");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const saveAdjust = async () => {
    setSaving(true);
    try {
      await api.patch(`/employees/${adjust.employee_id}/vacation-adjustment`, {
        adjustment_days: Number(adjustForm.adjustment_days) || 0,
        note: adjustForm.note,
      });
      toast.success("Saldo ajustado");
      setAdjust(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!requests) return <Loading label="Cargando solicitudes..." />;

  return (
    <div data-testid="solicitudes-page">
      <PageHeader title="Solicitudes" description="Vacaciones, permisos e incapacidades del personal" testid="solicitudes-header">
        <Button className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="new-request-button" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2" size={15} /> Nueva solicitud
        </Button>
      </PageHeader>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="rounded-sm bg-muted border border-border" data-testid="request-filter-tabs">
          <TabsTrigger value="all" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="filter-all">Todas</TabsTrigger>
          <TabsTrigger value="pending" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="filter-pending">Pendientes</TabsTrigger>
          <TabsTrigger value="approved" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="filter-approved">Aprobadas</TabsTrigger>
          <TabsTrigger value="rejected" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground" data-testid="filter-rejected">Rechazadas</TabsTrigger>
        </TabsList>

        <TabsContent value={filter} className="mt-4">
          {filtered.length === 0 ? (
            <EmptyState title="Sin solicitudes" description="No hay solicitudes con este estado." />
          ) : (
            <div className="border border-border rounded-sm overflow-x-auto bg-card">
              <table className="w-full text-sm" data-testid="requests-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
                    <th className="p-3 font-normal">Empleado</th>
                    <th className="p-3 font-normal">Tipo</th>
                    <th className="p-3 font-normal">Fechas</th>
                    <th className="p-3 font-normal">Días</th>
                    <th className="p-3 font-normal">Motivo</th>
                    <th className="p-3 font-normal">Estado</th>
                    <th className="p-3 font-normal text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((r) => (
                    <tr key={r.id} className="hover:bg-muted/70 transition-colors duration-200" data-testid={`request-row-${r.id}`}>
                      <td className="p-3">
                        <p className="text-foreground font-medium">{r.employee_name}</p>
                        <p className="text-xs text-muted-foreground">{r.employee_position}</p>
                      </td>
                      <td className="p-3 text-foreground/80">{typeLabel(r.type)}</td>
                      <td className="p-3 text-foreground/80">{fmtDate(r.start_date)} – {fmtDate(r.end_date)}</td>
                      <td className="p-3 text-foreground/80">{r.days}</td>
                      <td className="p-3 text-muted-foreground max-w-[220px] truncate" title={r.reason}>{r.reason || "—"}</td>
                      <td className="p-3"><StatusBadge status={r.status} /></td>
                      <td className="p-3 text-right">
                        <div className="inline-flex gap-1">
                          {r.status === "pending" && (
                            <>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-emerald-400 hover:bg-emerald-950/40" data-testid={`approve-${r.id}`}
                                onClick={() => { setResolve({ req: r, status: "approved" }); setComment(""); }}>
                                <Check size={15} />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:bg-red-950/30" data-testid={`reject-${r.id}`}
                                onClick={() => { setResolve({ req: r, status: "rejected" }); setComment(""); }}>
                                <X size={15} />
                              </Button>
                            </>
                          )}
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-400 hover:bg-red-950/30" data-testid={`delete-request-${r.id}`} onClick={() => setToDelete(r)}>
                            <Trash size={15} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <div className="mt-8 border border-border rounded-sm bg-card" data-testid="balances-card">
        <h3 className="font-heading text-lg font-bold text-foreground p-5 pb-4">Saldos de vacaciones</h3>
        {balances.length === 0 ? (
          <EmptyState title="Sin saldos" description="Los saldos se calculan con la fecha de ingreso de cada empleado." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="balances-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-y border-border">
                  <th className="p-3 font-normal">Empleado</th>
                  <th className="p-3 font-normal">Antigüedad</th>
                  <th className="p-3 font-normal">Derecho</th>
                  <th className="p-3 font-normal">Usados</th>
                  <th className="p-3 font-normal">Pendientes</th>
                  <th className="p-3 font-normal">Disponibles</th>
                  <th className="p-3 font-normal text-right">Ajuste</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {balances.map((b) => (
                  <tr key={b.employee_id} className="hover:bg-muted/70 transition-colors duration-200" data-testid={`balance-row-${b.employee_id}`}>
                    <td className="p-3 text-foreground font-medium">{b.full_name}</td>
                    <td className="p-3 text-foreground/80">{b.years_of_service} {b.years_of_service === 1 ? "año" : "años"}</td>
                    <td className="p-3 text-foreground/80">{b.entitled_days}</td>
                    <td className="p-3 text-foreground/80">{b.used_days}</td>
                    <td className="p-3 text-foreground/80">{b.pending_days}</td>
                    <td className="p-3 text-foreground font-medium">{b.available_days}</td>
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-accent" data-testid={`adjust-${b.employee_id}`}
                        onClick={() => { setAdjust(b); setAdjustForm({ adjustment_days: "", note: "" }); }}>
                        <Sliders size={15} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Nueva solicitud</DialogTitle>
          </DialogHeader>
          <form onSubmit={create} className="space-y-4" data-testid="request-form">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Empleado *</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger className={inputCls} data-testid="request-employee-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Tipo</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className={inputCls} data-testid="request-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Inicio *</Label>
                <Input type="date" required className={inputCls} data-testid="request-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Fin *</Label>
                <Input type="date" required className={inputCls} data-testid="request-end" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Motivo</Label>
              <Textarea className={`${inputCls} min-h-[70px]`} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="request-save-button">
                {saving && <CircleNotch className="animate-spin mr-2" size={15} />} Crear solicitud
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resolve} onOpenChange={() => setResolve(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">
              {resolve?.status === "approved" ? "Aprobar solicitud" : "Rechazar solicitud"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4" data-testid="resolve-dialog">
            <p className="text-sm text-muted-foreground">
              {resolve?.req.employee_name} · {typeLabel(resolve?.req.type)} · {fmtDate(resolve?.req.start_date)} – {fmtDate(resolve?.req.end_date)}
            </p>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Comentario</Label>
              <Textarea className={`${inputCls} min-h-[70px]`} data-testid="resolve-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Opcional" />
            </div>
            <DialogFooter>
              <Button onClick={doResolve} disabled={saving}
                className={`rounded-sm ${resolve?.status === "approved" ? "bg-primary text-primary-foreground hover:bg-primary/80" : "bg-red-600 text-white hover:bg-red-500"}`}
                data-testid="resolve-confirm-button">
                {saving && <CircleNotch className="animate-spin mr-2" size={15} />}
                {resolve?.status === "approved" ? "Aprobar" : "Rechazar"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!adjust} onOpenChange={() => setAdjust(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Ajustar saldo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4" data-testid="adjust-dialog">
            <p className="text-sm text-muted-foreground">{adjust?.full_name}</p>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Días de ajuste (+/-)</Label>
              <Input type="number" step="0.5" className={inputCls} data-testid="adjust-days" value={adjustForm.adjustment_days}
                onChange={(e) => setAdjustForm({ ...adjustForm, adjustment_days: e.target.value })} placeholder="Ej. 2 o -1.5" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Nota</Label>
              <Input className={inputCls} value={adjustForm.note} onChange={(e) => setAdjustForm({ ...adjustForm, note: e.target.value })} />
            </div>
            <DialogFooter>
              <Button onClick={saveAdjust} disabled={saving} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="adjust-save-button">
                {saving && <CircleNotch className="animate-spin mr-2" size={15} />} Aplicar ajuste
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!toDelete} onOpenChange={() => setToDelete(null)}
        title="Eliminar solicitud" description={`Se eliminará la solicitud de ${toDelete?.employee_name}.`}
        onConfirm={remove} testid="delete-request" />
    </div>
  );
}
